import { isBrowser, Log } from '@artup/utilities'
import { APISession } from './api-session'

const SESSION_CHANNEL_NAME = 'artup:session'

const tabId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
  ? crypto.randomUUID()
  : (Math.random().toString(36)
    .substring(2)) + Date.now().toString(36)

let channel = null
const listeners = new Set()
let storageListenerInitialized = false
let lastEventKey = null
let lastEventTime = 0

/**
 * Gets or creates the BroadcastChannel for cross-applet session events
 * @returns {BroadcastChannel|null}
 */
function getChannel () {
  if (!channel && isBrowser && typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(SESSION_CHANNEL_NAME)
      channel.onmessage = (event) => {
        const data = event.data
        if (data?.tabId && data.tabId === tabId) {
          return // Ignore messages broadcasted by the same window/tab
        }
        notifyListeners(data)
      }
    } catch (error) {
      Log.warn(`[session-sync] Failed to create BroadcastChannel: ${error.message}`)
    }
  }
  return channel
}

/**
 * Notifies all registered listeners of a session event
 * @param {Object} eventData
 */
function notifyListeners (eventData) {
  if (!eventData) return

  // Deduplicate rapid identical events across channels
  const token = eventData.token || eventData.session?.token || ''
  const eventKey = `${eventData.action}:${token}`
  const now = Date.now()
  if (eventKey === lastEventKey && (now - lastEventTime) < 1500) {
    return
  }
  lastEventKey = eventKey
  lastEventTime = now

  for (const listener of listeners) {
    try {
      listener(eventData)
    } catch (error) {
      Log.warn(`[session-sync] Listener error: ${error.message}`)
    }
  }
}

/**
 * Initializes the storage event listener as a fallback when BroadcastChannel is not supported
 */
function initStorageListener () {
  if (storageListenerInitialized || !isBrowser || typeof window === 'undefined' || !window.addEventListener) return
  // If BroadcastChannel is supported, rely on it to prevent duplicate event storms
  if (typeof BroadcastChannel !== 'undefined') return
  storageListenerInitialized = true

  window.addEventListener('storage', (event) => {
    if (event.key === APISession.tokenKey) {
      if (event.newValue) {
        notifyListeners({
          action: 'login',
          token: event.newValue,
          source: 'storage'
        })
      } else if (!event.newValue && event.oldValue) {
        notifyListeners({
          action: 'logout',
          source: 'storage'
        })
      }
    }
  })
}

/**
 * Broadcasts a session event to other tabs and applets
 * @param {Object} data
 * @param {String} data.action Action type: 'login' | 'logout'
 * @param {String} [data.token] Session token on login
 * @param {Account} [data.account] Account details
 * @param {Session} [data.session] Full session details
 */
export function broadcastSessionEvent (data = {}) {
  if (!isBrowser || !data?.action) return

  const ch = getChannel()
  if (ch) {
    try {
      ch.postMessage({ ...data, tabId })
    } catch (error) {
      Log.warn(`[session-sync] Failed to broadcast session event: ${error.message}`)
    }
  }
}

/**
 * Subscribes to cross-tab session events
 * @param {Function} listener Callback receiving { action, token, account, source }
 * @returns {Function} Unsubscribe function
 */
export function subscribeSessionEvents (listener) {
  if (!listener) return () => {}

  getChannel()
  initStorageListener()

  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}
